import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { useToast } from '../../context/ToastContext';
import DataTable from '../common/DataTable';
import Modal from '../common/Modal';
import InvoiceForm from '../common/InvoiceForm';
import PrintPreview from '../common/PrintPreview';
import { Plus, Printer, Edit2, Trash2 } from 'lucide-react';
import { formatCurrency, formatDate } from '../../utils/helpers';
import { isPurchaseBillLinked } from '../../utils/validation';

export default function PurchaseBill() {
  const { state, dispatch } = useApp();
  const { addToast } = useToast();
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [printData, setPrintData] = useState(null);

  const handleSave = (data) => {
    if (editData) {
      dispatch({ type: 'DELETE_PURCHASE_BILL', payload: editData.id });
      dispatch({ type: 'ADD_PURCHASE_BILL', payload: data });
      addToast('Purchase bill updated successfully', 'success');
    } else {
      dispatch({ type: 'ADD_PURCHASE_BILL', payload: data });
      addToast('Purchase bill created successfully', 'success');
    }
    closeForm();
  };

  const handleEdit = (row) => {
    const links = isPurchaseBillLinked(row, state);
    if (links.length > 0) {
      addToast(`Cannot edit ${row.entryNo}: it is linked to ${links.join(', ')}. Remove those entries first.`, 'error');
      return;
    }
    setEditData(row);
    setShowForm(true);
  };
  const handleDelete = (row) => {
    const links = isPurchaseBillLinked(row, state);
    if (links.length > 0) {
      addToast(`Cannot delete ${row.entryNo}: it is linked to ${links.join(', ')}. Remove those entries first.`, 'error');
      return;
    }
    if (window.confirm(`Delete purchase bill ${row.entryNo}? Stock and account effects will be reversed.`)) {
      dispatch({ type: 'DELETE_PURCHASE_BILL', payload: row.id });
      addToast('Purchase bill deleted', 'success');
    }
  };
  const closeForm = () => { setShowForm(false); setEditData(null); };

  const columns = [
    { key: 'entryNo', label: 'PB No', sortable: true },
    { key: 'date', label: 'Entry Date', sortable: true, render: (v) => formatDate(v) },
    { key: 'billDate', label: 'Bill Date', sortable: true, render: (v) => v ? formatDate(v) : '-' },
    { key: 'billNo', label: 'Supplier Bill No' },
    { key: 'partyId', label: 'Supplier', render: (v) => state.parties.find(p => p.id === v)?.name || '-' },
    { key: 'grandTotal', label: 'Amount', align: 'right', sortable: true, render: (v) => formatCurrency(v) },
    { key: 'status', label: 'Status', render: (v) => (
      <span className={`badge ${v === 'Paid' ? 'badge-success' : v === 'Partial' ? 'badge-warning' : 'badge-danger'}`}>{v}</span>
    )},
  ];

  if (printData) {
    return <PrintPreview type="purchaseBill" data={printData} onClose={() => setPrintData(null)} />;
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Purchase Bills</h1>
        <button onClick={() => { setEditData(null); setShowForm(true); }} className="btn btn-primary flex items-center gap-2">
          <Plus size={16} /> New Bill
        </button>
      </div>
      <div className="card">
        <DataTable
          columns={columns}
          data={[...state.purchaseBills].reverse()}
          searchFields={['entryNo', 'billNo', (r) => state.parties.find(p => p.id === r.partyId)?.name]}
          emptyState={{ title: 'No purchase bills yet', description: 'Create your first purchase bill.', actionLabel: 'New Bill', onAction: () => setShowForm(true) }}
          actions={(row) => (
            <>
              <button onClick={() => handleEdit(row)} className="p-1.5 rounded hover:bg-amber-50 text-amber-600" title="Edit"><Edit2 size={15} /></button>
              <button onClick={() => setPrintData(row)} className="p-1.5 rounded hover:bg-green-50 text-green-600" title="Print"><Printer size={15} /></button>
              <button onClick={() => handleDelete(row)} className="p-1.5 rounded hover:bg-red-50 text-red-600" title="Delete"><Trash2 size={15} /></button>
            </>
          )}
        />
      </div>
      <Modal isOpen={showForm} onClose={closeForm} title={editData ? `Edit Bill ${editData.entryNo}` : 'New Purchase Bill'} size="xl">
        <InvoiceForm
          key={editData ? editData.id : 'new'}
          type="purchase"
          onSave={handleSave}
          onCancel={closeForm}
          initialData={editData || undefined}
          nextEntryNo={editData ? editData.entryNo : `PB-${String(state.counters.purchaseBill + 1).padStart(4, '0')}`}
        />
      </Modal>
    </div>
  );
}
